import React, { useState, useEffect, useCallback } from 'react'
import ApplicantsPV from './ApplicantsPV'
import AuthA from '../../store/actions/AuthA'
import Papa from 'papaparse'
import axios from 'axios'
import sortBy from 'lodash/sortBy'
import find from 'lodash/find'
import upperCase from 'upper-case'
import moment from 'moment'
import {connect} from 'react-redux'
import { scoreNameMatch } from '../../utils/nameUtils'

// Custom useDebounce hook for smooth search performance
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Note: Simplified caching removed for React 16.8 compatibility

const _ = {
  sortBy, find
}

const changeCase = {
  upperCase
}

const ApplicantsPC = ({ auth, userType, authFn, onContentMount, history, location }) => {
  const [data, setData] = useState([])
  const [searchKey, setSearchKey] = useState('')
  const debouncedSearchKey = useDebounce(searchKey, 300) // 300ms delay for smooth typing
  
  // Simplified caching for React 16.8 compatibility (currently simplified)
  // const [cachedApplicants, updateApplicantsCache, clearApplicantsCache] = useSimpleCache('applicants')
  // const [cachedSchoolYears, updateSchoolYearsCache] = useSimpleCache('school_years')
  const [fields] = useState(['applicantNumber','type','lastName', 'firstName', 'email', 'paymentSchedule', 'AA', 'UE', 'clear', 'paid', 'actions'])
  const [activePage, setActivePage] = useState(1)
  const [itemPerPage] = useState(30)
  // const [selectedIndex, setSelectedIndex] = useState(0) // Unused
  const [column, setColumn] = useState(null)
  const [direction, setDirection] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [results, setResults] = useState([])
  const [modalImportOpen, setModalImportOpen] = useState(false)
  const [schoolYears, setSchoolYears] = useState([])
  const [students, setStudents] = useState([])
  const [paymentFile, setPaymentFile] = useState(null)
  const [gradeFile, setGradeFile] = useState(null)
  const [gradeFilename, setGradeFilename] = useState('')
  const [enrollmentFile, setEnrollmentFile] = useState(null)
  // const [enrollmentFilename, setEnrollmentFilename] = useState('') // Currently unused
  const [gradeLevel, setGradeLevel] = useState('Grade 1')
  const [schoolYearId, setSchoolYearId] = useState('')

  const getApplicants = useCallback(() => {
    const loadingTimeout = setTimeout(() => setLoaded(false), 100); // Small delay to prevent flickering
    
    let apiUrl = `${process.env.REACT_APP_URL}/applicants?filter[where][active]=true&filter[where][schoolYearId]=${schoolYearId}&filter[order]=created%20DESC&filter[limit]=1000`;
    
    // Add grade level filter to API call if grade level is selected
    if (gradeLevel && gradeLevel !== '') {
      apiUrl += `&filter[where][gradeLevel]=${encodeURIComponent(gradeLevel)}`;
    }
    
    axios.get(apiUrl)
    .then(response => {
      clearTimeout(loadingTimeout);
      setData(response.data)
      setLoaded(true)
    })
    .catch(err => {
      clearTimeout(loadingTimeout);
      console.log(err)
      setLoaded(true) // Still set loaded to true to show error state
    })
  }, [schoolYearId, gradeLevel])

  const getSchoolYears = useCallback(() => {
    axios.get(process.env.REACT_APP_URL+'/schoolyears?filter[where][active]=true')
    .then(response => {
      const currentSchoolYear = _.find(response.data, {
        isCurrent: true
      });
      setSchoolYears(response.data)
      setSchoolYearId(currentSchoolYear.schoolYearId)
    });
  }, [])

  const getOldStudent = useCallback((firstName, lastName) => {
    const handleSearchError = (error) => {
      console.log(error);
      setStudents([]);
      if (error.response) {
        const errorMessage = error.response.data && error.response.data.error && error.response.data.error.message
          ? error.response.data.error.message
          : 'Unknown error';
        alert(`Error searching for students: ${errorMessage}`);
      } else if (error.request) {
        alert('Network error while searching for students');
      } else {
        alert('Error searching for students. Please contact your administrator.');
      }
    };

    // Fallback for older backends without the /students/searchOldStudent
    // endpoint: fetch the active-student roster (light field projection) and
    // rank entirely client-side. We can't rely on the server `like` query to
    // pre-filter — it's accent- and typo-sensitive, so a stored "Piñgul" never
    // matches a typed "Pingul", and any first/last split difference makes both
    // OR clauses miss. Scoring with scoreNameMatch handles accents, typos,
    // spacing, and name-order.
    const rankAgainstRoster = () => {
      const filter = {
        where: { active: true },
        fields: { studentId: true, firstName: true, middleName: true, lastName: true, lrn: true, studentNumber: true },
        limit: 100000,
      };

      axios.get(`${process.env.REACT_APP_URL}/students`, { params: { filter: JSON.stringify(filter) } })
      .then(response => {
        const scored = response.data
          .map(student => ({
            student,
            score: scoreNameMatch(firstName, lastName, student.firstName, student.lastName),
          }))
          .sort((a, b) => b.score - a.score);

        const MATCH_THRESHOLD = 0.6;
        const strong = scored.filter(x => x.score >= MATCH_THRESHOLD);
        const finalList = (strong.length > 0 ? strong : scored.slice(0, 10)).map(x => x.student);

        setStudents(finalList);

        if (response.data.length === 0) {
          alert(`No matching students found for "${firstName} ${lastName}"`);
        } else if (strong.length === 0) {
          alert(`No strong matches for "${firstName} ${lastName}". Showing the closest results — please verify carefully.`);
        }
      })
      .catch(handleSearchError);
    };

    // Prefer the server-side matcher: it does the same accent/typo-tolerant
    // ranking on the backend and returns only the top candidates, so the client
    // never downloads the whole roster. The dropdown order mirrors this array,
    // so ranking is preserved. We never auto-link — the registrar always picks.
    axios.get(`${process.env.REACT_APP_URL}/students/searchOldStudent`, { params: { firstName, lastName } })
    .then(response => {
      setStudents(response.data);
      if (response.data.length === 0) {
        alert(`No matching students found for "${firstName} ${lastName}"`);
      }
    })
    .catch(error => {
      // Endpoint not deployed yet (older backend) → fall back to client ranking.
      const status = error.response && error.response.status;
      if (status === 404 || status === 405 || status === 501) {
        rankAgainstRoster();
      } else {
        handleSearchError(error);
      }
    });
  }, [])

  const isSearched = useCallback((searchKey) => {
    if (!searchKey) return () => true;
    
    const lowerSearchKey = searchKey.toLowerCase();
    
    return function(item) {
      const firstName = (item.firstName && item.firstName.toLowerCase()) || '';
      const lastName = (item.lastName && item.lastName.toLowerCase()) || '';
      const appNumber = item.appNumber || '';
      
      return firstName.includes(lowerSearchKey) || 
             lastName.includes(lowerSearchKey) || 
             appNumber.includes(searchKey);
    };
  }, [])

  const handleChange = useCallback((e, { name, value }) => {
    if (name === 'gradeLevel') {
      setGradeLevel(value)
      setLoaded(false) // Show loading when changing grade level
    }
    // Add other field handlers as needed
  }, [])

  const handleChangeSchoolYear = useCallback((e, { value }) => {
    setSchoolYearId(value)
    setLoaded(false)
  }, [])

  const onSearchChange = useCallback((event) => {
    setSearchKey(event.target.value) // This updates immediately for UI responsiveness
  }, [])

  const handleSort = useCallback((clickedColumn) => {
    if (column !== clickedColumn) {
      setColumn(clickedColumn)
      setData(_.sortBy(data, [clickedColumn]))
      setDirection('ascending')
      return
    }
    setData([...data].reverse())
    setDirection(direction === 'ascending' ? 'descending' : 'ascending')
  }, [column, data, direction])

  const handlePaginationChange = useCallback((e, {activePage}) => {
    setActivePage(activePage)
  }, [])

  const handleFileChange = useCallback((file) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: handleDataChange
    });
  }, [])

  const handleDataChange = useCallback((results) => {
    setResults(results.data)
    console.log('Parsing complete:', results);
  }, [])

  const handleRowClick = useCallback((applicant) => {
    history.push(location.pathname+'/' + applicant.applicantId);
  }, [history, location])

  const handleDataSave = useCallback(async () => {
    const csvData = results;
    let parsedapplicant = {}, successCount = 0;

    for (let i = 0; i < csvData.length; i++) {
      parsedapplicant = {
        firstName: csvData[i]['firstName'],
        middleName: csvData[i]['middleName'],
        lastName: csvData[i]['lastName'],
        nickName: csvData[i]['nickName'],
        gender: csvData[i]['gender'],
        birthDate: moment(csvData[i]['birthDate']),
        address: csvData[i]['address'],
        email: csvData[i]['email'],
        lrn: csvData[i]['lrn'],
        applicantNumber: csvData[i]['applicantNumber'],
        religion: csvData[i]['religion'],
        placeOfBirth: csvData[i]['placeOfBirth'],
        mobile1: csvData[i]['mobile1'],
        mobile2: csvData[i]['mobile2'],
        contactPerson: csvData[i]['contactPerson'],
        contactPersonNumber: csvData[i]['contactPersonNumber'],
        contactPersonAddress: csvData[i]['contactPersonAddress'],
        guardianName: csvData[i]['guardianFirstName'] + " " + csvData[i]['guardianMiddleName'] + " " + csvData[i]['guardianLastName'] + " " + csvData[i]['guardianExtensionName'],
        guardianAddress: csvData[i]['guardianAddress'],
        guardianOccupation: csvData[i]['guardianOccupation'],
        guardianOfficeAddress: csvData[i]['guardianOfficeAddress'],
        guardianNumber: csvData[i]['guardianNumber'],
        beenHospitalized: csvData[i]['beenHospitalized'],
        hospitalReason: csvData[i]['hospitalReason'],
        medicalHistory: csvData[i]['medicalHistory'],
      };
      const url = process.env.REACT_APP_URL+'/applicants';
      try {
        await axios.request({
          method: 'post',
          url: url,
          data: parsedapplicant
        })
        console.log(`Successfully loaded:${i+1}`);
        successCount++;
      } catch(error){
        console.log(error)
        if (error.response) {
          alert(`Line ${i+2}: ${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
        setModalImportOpen(false)
        getApplicants()
        break;
      }
    }
    alert(`${successCount} applicant/s successfully uploaded.`)
    setModalImportOpen(false)
    getApplicants()
  }, [results, getApplicants])

  const handleSubmit = useCallback((data) => {
    const url = process.env.REACT_APP_URL+'/applicants';
    axios
      .request({
        method: 'post',
        url: url,
        data: data
      })
      .then(response => {
        alert(`Successfully added an applicant.`)
        getApplicants()
      })
      .catch(error => {
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
        getApplicants()
      });
  }, [getApplicants])

  const handleUploadEnrollmentFile = useCallback(() => {
    const url = process.env.REACT_APP_URL+'/assets/upload?module=enrollment';
    let fileData = new FormData();
    fileData.append('filePath', enrollmentFile);
    axios
      .request({
        method: 'post',
        url: url,
        data: fileData
      })
      .then(response => {
        alert(`Requirements was successfully saved.`);
      })
      .catch(error => {
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
      });
  }, [enrollmentFile])

  const handleUploadGradeFile = useCallback(() => {
    const url = process.env.REACT_APP_URL+'/assets/upload?module=reportCard';
    let fileData = new FormData();
    fileData.append('filePath', gradeFile);
    axios
      .request({
        method: 'post',
        url: url,
        data: fileData
      })
      .then(response => {
        alert(`Report card was successfully saved.`);
      })
      .catch(error => {
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
      });
  }, [gradeFile])

  const handleUpdate = useCallback((data, id) => {
    const url = process.env.REACT_APP_URL+'/applicants/'+id;
    axios
      .request({
        method: 'put',
        url: url,
        data: data
      })
      .then(response => {
        alert(`Successfully updated an applicant.`)
        if(gradeFile !== null) {
          handleUploadGradeFile();
        }
        if(enrollmentFile !== null) {
          handleUploadEnrollmentFile();
        }
        getApplicants()
      })
      .catch(error => {
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
        getApplicants()
      });
  }, [gradeFile, enrollmentFile, getApplicants, handleUploadGradeFile, handleUploadEnrollmentFile])

  const handleDelete = useCallback((id) => {
    const url = process.env.REACT_APP_URL+'/applicants/'+id;
    axios
      .request({
        method: 'put',
        url: url,
        data: {active: false}
      })
      .then(response => {getApplicants()})
      .catch(err => console.log(err));
  }, [getApplicants])

  const handleUploadEnrollmentFileChange = useCallback((enrollmentFile, applicant) => {
    const { firstName, lastName, gradeLevel } = applicant;
    const fileType = enrollmentFile.type;
    const fileTypeArray = fileType.split('/');
    const type = fileTypeArray[1];
    const fileName = `${changeCase.upperCase(lastName)}-${changeCase.upperCase(firstName)}-${changeCase.upperCase(gradeLevel)}.${type}`;
    const file = new File([enrollmentFile], fileName, { type: enrollmentFile.type });

    setEnrollmentFile(file)
    // setEnrollmentFilename(fileName) // Currently unused
  }, [])

  const handleUploadGradeFileChange = useCallback((gradeFile, applicant) => {
    const { firstName, lastName } = applicant;
    const fileType = gradeFile.type;
    const fileTypeArray = fileType.split('/');
    const type = fileTypeArray[1];
    const fileName = `${changeCase.upperCase(lastName)}-${changeCase.upperCase(firstName)}-REPORTCARD.${type}`;
    const file = new File([gradeFile], fileName, { type: gradeFile.type });

    setGradeFile(file)
    setGradeFilename(fileName)
  }, [])

  // Upload functions moved above to fix order issue
  
  const handleSaveAsStudent = useCallback((data) => {
    const url = process.env.REACT_APP_URL+'/students';
    // Some applicants (CSV-imported / legacy) only carry `appNumber`, not
    // `applicantNumber`. Guard against a null so we don't throw a silent
    // TypeError on .substring before any request is made.
    const rawNumber = data.applicantNumber || data.appNumber;
    if (!rawNumber) {
      alert('Cannot save as student: this applicant has no applicant number. Please set an applicant number before saving as a student.');
      return;
    }
    // Derive studentNumber by dropping the 4-char (year) prefix.
    const studentNumber = String(rawNumber).substring(4);
    // Build a new payload instead of mutating the applicant state object.
    const payload = { ...data, studentNumber };
    console.log(payload)
    axios
      .request({
        method: 'post',
        url: url,
        data: payload
      })
      .then(response => {
        alert(`Applicant ${response.data.lastName}, ${response.data.firstName} was succesfully saved as student!`);
        axios
          .request({
            method: 'put',
            url: process.env.REACT_APP_URL+'/applicants/'+data.applicantId,
            data: {isStudent: true, studentId: response.data.studentId}
          })
          .then(response => {
            alert(`Successfully updated an applicant.`)
            getApplicants()
          })
          .catch(error => {
            console.log(error)
            if (error.response) {
              alert(`${error.response.data.error.message}`);
            } else if (error.request) {
              alert(error.request);
            } else {
              alert('Error. Please contact your administrator for assistance.');
            }
            getApplicants()
          });
      })
      .catch(error => {
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
        getApplicants()
      });
  }, [getApplicants])

  const handleSaveAsOldStudent = useCallback((data, studentId) => {
    if (!studentId) {
      alert('Please select the matching student from the "Possible Students" dropdown before saving as a student.');
      return;
    }
    axios
      .request({
        method: 'put',
        url: process.env.REACT_APP_URL+'/applicants/'+data.applicantId,
        data: {isStudent: true, studentId: studentId}
      })
      .then(response => {
        alert(`Successfully updated an applicant.`)
        getApplicants()
      })
      .catch(error => {
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
        getApplicants()
      });
  }, [getApplicants])

  const handleUploadPaymentFile = useCallback(() => {
    const url = process.env.REACT_APP_URL+'/assets/upload?module=payments';
    let fileData = new FormData();
    fileData.append('filePath', paymentFile);
    axios
      .request({
        method: 'post',
        url: url,
        data: fileData
      })
      .then(response => {
        alert(`OR Image was successfully saved.`);
      })
      .catch(error => {
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
      });
  }, [paymentFile])

  const handleAddStudentToSY = useCallback((schoolYearId, studentId, applicantId, gradeLevel) => {
    if (!studentId) {
      alert('This applicant is not linked to a student record yet. Use "Get Possible Student", pick the matching student, and save as a student first.');
      return;
    }
    // A blank school year used to be accepted here and stored as an empty string,
    // which made the student invisible in every school year of the Students list.
    if (!schoolYearId) {
      alert('Please select a school year before adding the student to it.');
      return;
    }
    const url = process.env.REACT_APP_URL+'/students/addSchoolYear';
    axios
      .request({
        method: 'put',
        url: url,
        data: {schoolYearId, studentId, gradeLevel}
      })
      .then(response => {
        alert(`Student successfully added to the school year`);
        axios
          .request({
            method: 'put',
            url: process.env.REACT_APP_URL+'/applicants/'+applicantId,
            data: {isCurrentStudent: true}
          })
          .then(response => {
            alert(`Finished application.`)
            getApplicants()
          })
          .catch(error => {
            console.log(error)
            if (error.response) {
              alert(`${error.response.data.error.message}`);
            } else if (error.request) {
              alert(error.request);
            } else {
              alert('Error. Please contact your administrator for assistance.');
            }
            getApplicants()
          });
      })
      .catch(error => {
        console.log('pasok sa error')
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
        getApplicants()
      });
  }, [getApplicants])

  const handleStudentPayment = useCallback((data, applicantId, isPaymentUponEnrollment) => {
    const url = process.env.REACT_APP_URL+'/payments';
    let d;
    if (isPaymentUponEnrollment) {
      d = {isPaymentDone: true, isPaid: true};
    } else {
      d = {isReservationDone: true, isPartiallyPaid: true};
    }
    axios
      .request({
        method: 'post',
        url: url,
        data
      })
      .then(response => {
        alert(`Payment for student successfully saved!`);
        if(paymentFile !== null) {
          handleUploadPaymentFile();
        }
        axios
          .request({
            method: 'put',
            url: process.env.REACT_APP_URL+'/applicants/'+applicantId,
            data: d
          })
          .then(response => {
            getApplicants()
            alert(`Notify the registrar to include the student to the school year.`)
          })
          .catch(error => {
            console.log(error)
            if (error.response) {
              alert(`${error.response.data.error.message}`);
            } else if (error.request) {
              alert(error.request);
            } else {
              alert('Error. Please contact your administrator for assistance.');
            }
            getApplicants()
          });
      })
      .catch(error => {
        console.log(error)
        if (error.response) {
          alert(`${error.response.data.error.message}`);
        } else if (error.request) {
          alert(error.request);
        } else {
          alert('Error. Please contact your administrator for assistance.');
        }
        getApplicants()
      });
  }, [paymentFile, handleUploadPaymentFile, getApplicants])

  const handleUploadFileChange = useCallback((file) => {
    setPaymentFile(file)
  }, [])

  const handleModalImportOpen = useCallback((open) => {
    setModalImportOpen(open)
  }, [])

  // useEffect hooks
  useEffect(() => {
    authFn.getToken()
    onContentMount({title:'Applicants'})
    getSchoolYears()
  }, [authFn, onContentMount, getSchoolYears])

  // Effect to get applicants when schoolYearId or gradeLevel changes (instant loading!)
  useEffect(() => {
    if (schoolYearId) {
      getApplicants()
    }
  }, [schoolYearId, gradeLevel, getApplicants])


  return (
    <ApplicantsPV
      auth={auth}
      userType={userType}
      activePage={activePage}
      itemPerPage={itemPerPage}
      column={column}
      direction={direction}
      data={data}
      searchKey={searchKey}
      debouncedSearchKey={debouncedSearchKey}
      fields={fields}
      loaded={loaded}
      schoolYears={schoolYears}
      students={students}
      paymentFile={paymentFile}
      enrollmentFilename={enrollmentFile}
      gradeFilename={gradeFilename}
      modalImportOpen={modalImportOpen}
      gradeLevel={gradeLevel}
      schoolYearId={schoolYearId}
      handleChangeSchoolYear={handleChangeSchoolYear}
      handleModalImportOpen={handleModalImportOpen}
      isSearched={isSearched}
      onSearchChange={onSearchChange}
      handleSort={handleSort}
      handlePaginationChange={handlePaginationChange}
      handleFileChange={handleFileChange}
      handleDataSave={handleDataSave}
      handleRowClick={handleRowClick}
      handleSubmit={handleSubmit}
      handleUpdate={handleUpdate}
      handleDelete={handleDelete}
      getApplicants={getApplicants}
      handleSaveAsStudent={handleSaveAsStudent}
      handleSaveAsOldStudent={handleSaveAsOldStudent}
      handleAddStudentToSY={handleAddStudentToSY}
      handleStudentPayment={handleStudentPayment}
      handleUploadFileChange={handleUploadFileChange}
      handleUploadGradeFileChange={handleUploadGradeFileChange}
      handleUploadEnrollmentFileChange={handleUploadEnrollmentFileChange}
      getOldStudent={getOldStudent}
      handleChange={handleChange}
    />
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    authFn: AuthA(dispatch)
  }
}

const mapStateToProps = (state) => {
  return {
    auth: state.AuthR.auth,
    userType: state.AuthR.userType
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ApplicantsPC);
